import React from 'react';
import { motion } from 'framer-motion';
import { Monitor, FileText, Activity, ExternalLink } from 'lucide-react';
import { trackAffiliateClick } from '../utils/metaPixel';
export function ProCreditWatchSection() {
  return <section className="py-24 lg:py-36 bg-offwhite relative" data-id="element-402">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" data-id="element-403">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, #64748b 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-404"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-405">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-406">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6" data-id="element-407">
            <span className="text-navy text-xs font-bold tracking-widest uppercase" data-id="element-408">
              Monitoring Systems Reviewed
            </span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold font-serif text-navy mb-6 tracking-tight" data-id="element-409">
            Monitor bureau activity while managing disputes with documented
            visibility
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-lg text-slate leading-[1.75] max-w-[65ch] mx-auto" data-id="element-410">
            Built for consumers who want daily monitoring, organized
            documentation, and transparent reporting workflows.
          </motion.p>
        </div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-411">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-412">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-5" data-id="element-413">
              <Monitor className="w-6 h-6 text-emerald-600" data-id="element-414" />
            </div>
            <div className="border-t border-slate-100 pt-4" data-id="element-415">
              <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-416">Monitor</h3>
              <p className="text-slate leading-[1.75]" data-id="element-417">
                Daily FICO 8 monitoring across all three major consumer
                reporting bureaus.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-418">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-5" data-id="element-419">
              <FileText className="w-6 h-6 text-emerald-600" data-id="element-420" />
            </div>
            <div className="border-t border-slate-100 pt-4" data-id="element-421">
              <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-422">Document</h3>
              <p className="text-slate leading-[1.75]" data-id="element-423">
                AI-assisted drafting tools reviewed and submitted directly by
                the consumer.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-424">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-5" data-id="element-425">
              <Activity className="w-6 h-6 text-emerald-600" data-id="element-426" />
            </div>
            <div className="border-t border-slate-100 pt-4" data-id="element-427">
              <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-428">Watch</h3>
              <p className="text-slate leading-[1.75]" data-id="element-429">
                Track bureau updates, investigations, and reporting changes from
                one centralized timeline.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-12" data-id="element-430">
          <div className="overflow-x-auto" data-id="element-431">
            <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-432">
              <thead data-id="element-433">
                <tr className="border-b border-slate-200" data-id="element-434">
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs" data-id="element-435">
                    Feature
                  </th>
                  <th className="py-5 px-6 font-bold text-emerald-800 bg-emerald-50 border-l-4 border-emerald-600 w-1/4 uppercase tracking-wide text-xs relative" data-id="element-436">
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-437">
                      RECOMMENDED
                    </span>
                    ProCreditWatch ($59.99/mo)
                  </th>
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs border-l border-slate-100" data-id="element-438">
                    Traditional Credit Repair ($150-$300/mo)
                  </th>
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs border-l border-slate-100" data-id="element-439">
                    Free Monitoring ($0)
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-440">
                <tr className="border-b border-slate-100" data-id="element-441">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-442">
                    Monthly Cost
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-443">
                    Flat $59.99 subscription
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-444">
                    High recurring retainers
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-445">
                    Free
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-446">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-447">
                    What You SEE
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-448">
                    Daily bureau visibility
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-449">
                    Limited internal visibility
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-450">
                    Basic score estimates
                  </td>
                </tr>
                <tr className="border-b border-slate-100" data-id="element-451">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-452">
                    What They DO
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-453">
                    Consumer-directed workflows
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-454">
                    Outsourced dispute activity
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-455">
                    Monitoring only
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-456">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-457">
                    Who Controls It
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-458">
                    Consumer reviews every submission
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-459">
                    Third-party operators
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-460">
                    Platform alerts only
                  </td>
                </tr>
                <tr className="border-b border-slate-100" data-id="element-461">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-462">
                    Time to Visibility
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-463">
                    Daily updates
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-464">
                    Often unclear
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-465">
                    Delayed or generalized
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-466">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-467">
                    Transparency
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-468">
                    Centralized activity history
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-469">
                    Varies by provider
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-470">
                    Limited reporting detail
                  </td>
                </tr>
                <tr data-id="element-471">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-472">
                    Action Layer
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-473">
                    Drafting + monitoring infrastructure
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-474">
                    Outsourced process
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-475">
                    No structured workflow
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        <div className="text-center" data-id="element-476">
          <a href="https://procreditwatch.com/redirect.asp?guid=ZMEA756999SU" target="_blank" rel="noopener sponsored" data-product="procreditwatch" onClick={() => trackAffiliateClick('procreditwatch', 'ProCreditWatch')} className="inline-flex items-center justify-center bg-emerald hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-477">
            View Monitoring Infrastructure
            <ExternalLink className="ml-2 w-5 h-5" data-id="element-478" />
          </a>
          <p className="mt-4 text-sm text-slate-500 italic" data-id="element-479">
            No score guarantees. Consumers review and submit all disputes
            independently.
          </p>
        </div>
      </div>
    </section>;
}